import React, { useRef, useEffect } from "react";
import { useLoadOnVisible, useIsVisible } from "../../hooks";
import { applyVideoPlayback } from "./videoPlayback.helpers";
import { videoDecodeLimiter } from "./videoDecodeLimiter";

const VideoPlayer = ({
  videoSrc,
  poster,
  className = "",
  type = "video/mp4",
  shouldPlay = true,
}) => {
  const ref = useRef(null);
  // Inject the source once when it first nears the viewport (don't re-download
  // on every scroll); keep a live visibility signal to drive play/pause.
  const isInView = useLoadOnVisible(ref, { rootMargin: "200px" });
  const isVisible = useIsVisible(ref, { rootMargin: "50px" });

  // Once the source is attached, calling play() (when visible) is also what
  // kicks off loading for preload="none" — so gate on isInView, not on a
  // loadeddata event that would never fire until something starts the load.
  // `shouldPlay` lets a parent (e.g. a carousel) keep all but the active slide
  // on its poster frame so iOS never decodes more than one video at a time.
  useEffect(() => {
    applyVideoPlayback(ref.current, {
      isLoaded: isInView,
      isVisible,
      shouldPlay,
      limiter: videoDecodeLimiter,
    });
  }, [isInView, isVisible, shouldPlay]);

  // Free the shared decode slot when this player unmounts (e.g. a slick slide
  // that lazyLoad drops) so the budget isn't held by a gone element.
  useEffect(() => {
    const el = ref.current;
    return () => videoDecodeLimiter.release(el);
  }, []);

  return (
    <video
      ref={ref}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      className={className}
      height="480"
      width="640"
    >
      {isInView && <source src={videoSrc} type={type} />}
    </video>
  );
};

export default VideoPlayer;
